import moment from 'moment';
import type { PromotionEntry, PromotionType } from '@common/types/promotions';
import { matchesPromotionRoutes } from './route-matching';
import { evaluateAudienceForUser } from './evaluate-audience';
import type { AudienceEvaluationContext } from './build-audience-context';

export interface PromotionVisibilityContext {
  pathname: string;
  dismissedKeys: string[];
  audienceContext: AudienceEvaluationContext;
  now?: moment.Moment;
}

function isWithinTimeWindow(
  entry: PromotionEntry,
  now: moment.Moment
): boolean {
  const start = moment(entry.startAt);
  const end = moment(entry.endAt);

  if (!start.isValid() || !end.isValid()) return false;

  return now.isSameOrAfter(start) && now.isSameOrBefore(end);
}

export function isPromotionVisible(
  entry: PromotionEntry,
  context: PromotionVisibilityContext
): boolean {
  if (!entry.enabled) return false;

  const now = context.now ?? moment();
  if (!isWithinTimeWindow(entry, now)) return false;

  if (entry.type === 'bar' && context.dismissedKeys.includes(entry.key))
    return false;

  if (
    !matchesPromotionRoutes(
      context.pathname,
      entry.routes,
      entry.excludedRoutes
    )
  ) {
    return false;
  }

  return evaluateAudienceForUser(entry.audience, context.audienceContext, now);
}

export function getVisiblePromotionsByType(
  items: PromotionEntry[],
  type: PromotionType,
  context: PromotionVisibilityContext
): PromotionEntry[] {
  return items.filter(
    (entry) => entry.type === type && isPromotionVisible(entry, context)
  );
}

export function getActiveAnnouncementBar(
  items: PromotionEntry[],
  context: PromotionVisibilityContext
): PromotionEntry | null {
  const visibleBars = getVisiblePromotionsByType(items, 'bar', context);

  if (visibleBars.length === 0) return null;

  return visibleBars.reduce((best, current) => {
    const bestPriority = best.priority ?? 0;
    const currentPriority = current.priority ?? 0;

    if (currentPriority > bestPriority) return current;
    if (currentPriority < bestPriority) return best;

    const bestIndex = items.indexOf(best);
    const currentIndex = items.indexOf(current);
    return currentIndex < bestIndex ? current : best;
  });
}

export function getVisiblePromotionByKey(
  items: PromotionEntry[],
  key: string,
  context: PromotionVisibilityContext
): PromotionEntry | null {
  const entry = items.find((item) => item.key === key);
  if (!entry || entry.type !== 'badge') return null;

  return isPromotionVisible(entry, context) ? entry : null;
}
